/* eslint-disable jsx-a11y/no-noninteractive-tabindex */

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

/* eslint-disable import/prefer-default-export */
import clamp from 'lodash/clamp';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';

import { legacyTranslations } from '@components/RankingOrder/constants';

import layoutStyles from '@css/layout.scss';

import { isRankingOrderAccessibilityEnabled } from '@services/featureFlags';
import { LEGACY_replaceVariables, getTranslation, replaceVariables } from '@services/translations';

import stripHtml from '@utils/stripHtml';

import { CustomDragLayer } from './CustomDragLayer';
import { ReorderableItem } from './ReorderableItem';
import styles from './ReorderableList.scss';
import { translations } from './constants';
import { defaultReorderHandler } from './helpers';

const RESERVED_KEYS = ['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Escape', 'Enter', ' '];

export class ReorderableList extends React.PureComponent {
  static propTypes = {
    /** List of items that will be rendered */
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired
      })
    ).isRequired,
    /** Function that receives the element index and returns the rendered item */
    children: PropTypes.func,
    /** Function that receives the element index and returns the preview for that element */
    getPreview: PropTypes.func,
    /** Callback to handle reordering, passed in automatically */
    onReorder: PropTypes.func.isRequired,
    /** Method to get DOM ref of dragged item */
    getRef: PropTypes.func,
    caption: PropTypes.string
  };

  static defaultProps = {
    children: noop,
    getPreview: null,
    getRef: null
  };

  // ReorderableList.Preview = Preview;
  static defaultReorderHandler = defaultReorderHandler;
  liveRegionRef = React.createRef<HTMLSpanElement>();

  constructor(props) {
    super(props);
    this.state = {
      dropTargetIndex: null,
      dragStartIndex: null,
      ariaLiveMessage: '',
      itemOrder: [],
      selectedIndex: undefined,
      focusedIndex: undefined,
      showDragLayer: false
    };
  }

  componentDidUpdate() {
    const { focusedIndex } = this.state;
    if (this.state.listRef) {
      if (this.handleBlur === null) {
        this.handleBlur = () => {
          if (isRankingOrderAccessibilityEnabled()) {
            this.liveRegionRef.current.textContent = ' ';
          }
          /**
           * Without requestAnimationFrame (or setTimeout),
           * document.activeElement always returns <body> regardless of event target
           * Reference: https://developer.mozilla.org/en-US/docs/Web/API/DocumentOrShadowRoot/activeElement */
          requestAnimationFrame(() => {
            if (!this.state.listRef.contains(document.activeElement)) {
              this.setFocusedIndex(undefined);
              this.setSelectedIndex(undefined);
            }
          });
        };
        this.state.listRef.addEventListener('blur', this.handleBlur, true);
      }
      /* find all child draggable elements */
      const foundItems = this.state.listRef.querySelectorAll('[role="listitem"]');
      /* focus child element, if exists */
      if (foundItems && focusedIndex !== undefined && !Number.isNaN(focusedIndex)) {
        const currentFocusedItem = foundItems.item(focusedIndex);
        if (currentFocusedItem) {
          currentFocusedItem.focus();
        }
      }
    }
  }

  componentWillUnmount() {
    this.state.listRef.removeEventListener('blur', this.handleBlur);
  }

  onDragTargetUpdate = (updated) => {
    this.setState(updated);
  };

  onDragStart = (rowIndex) => {
    this.setState({
      dragStartIndex: rowIndex,
      focusedIndex: undefined,
      selectedIndex: undefined
    });

    setTimeout(() => {
      this.setState({ showDragLayer: true });
    }, 0);
  };

  onReorder = (rowIndex, targetIndex) => {
    const focusIndex = targetIndex !== null ? targetIndex : rowIndex;
    this.props.onReorder(rowIndex, targetIndex);
    this.onDrop();
    this.resetState();
    this.setFocusedIndex(focusIndex);
  };

  onDrop = () => {
    this.setState({
      dropTargetIndex: null,
      dragStartIndex: null
    });

    setTimeout(() => {
      this.setState({ showDragLayer: false });
    }, 0);
  };

  setFocusedIndex = (index) => {
    this.setState({ focusedIndex: index });
  };

  setSelectedIndex = (index) => {
    this.setState({ selectedIndex: index });
  };

  setItemOrder = (values) => {
    this.setState({ itemOrder: values });
  };

  setAriaLiveMessage = (message) => {
    this.setState({ ariaLiveMessage: message });
  };

  setAriaLiveMessageWithRankingOrderAccessibility = (message: string) => {
    if (this.liveRegionRef.current) {
      this.liveRegionRef.current.textContent = ' ';
      setTimeout(() => {
        this.liveRegionRef.current.textContent = message;
      }, 0);
    }
  };

  setIsDragging = (isDragging) => {
    if (isDragging === this.state.isDragging) return;

    this.setState((prevState) => ({
      ...prevState,
      isDragging
    }));
  };

  resetState = () => {
    this.setState({ selectedIndex: undefined, itemOrder: [] });
  };

  handleBlur = null;

  handleKeyDown = (event) => {
    if (RESERVED_KEYS.indexOf(event.key) === -1) {
      return;
    }

    const { focusedIndex, selectedIndex, itemOrder } = this.state;
    const { items } = this.props;
    const actualItems = itemOrder.length ? itemOrder : items;

    event.preventDefault();

    switch (event.key) {
      case ' ':
        /* Space bar will initiate the select operation or drop the item */
        if (selectedIndex !== undefined && focusedIndex !== undefined) {
          this.setAriaLiveMessage(
            LEGACY_replaceVariables(legacyTranslations().GRABBED_ITEM_DROP, [
              actualItems[focusedIndex].text,
              focusedIndex + 1,
              items.length
            ])
          );
          this.onReorder(selectedIndex, focusedIndex);
        } else {
          this.setSelectedIndex(focusedIndex);
          this.setAriaLiveMessage(
            LEGACY_replaceVariables(legacyTranslations().ITEM_GRABBED, [
              actualItems[focusedIndex].text,
              focusedIndex + 1,
              items.length
            ])
          );
        }
        break;
      case 'ArrowUp': {
        /* If a selected item exists, decrements position. Always updates focus */
        const prevSelectedItem = clamp(
          focusedIndex !== undefined ? focusedIndex - 1 : 0,
          0,
          items.length - 1
        );
        if (selectedIndex !== undefined && focusedIndex !== undefined) {
          this.setAriaLiveMessage(
            LEGACY_replaceVariables(legacyTranslations().GRABBED_ITEM_MOVE, [
              (actualItems[focusedIndex].text, prevSelectedItem + 1, items.length)
            ])
          );

          this.setItemOrder(defaultReorderHandler(actualItems, focusedIndex, prevSelectedItem));
        }
        this.setFocusedIndex(prevSelectedItem);
        break;
      }
      case 'ArrowDown': {
        /* If a selected item exists, increments position. Always updates focus */
        const nextSelectedItem = clamp(
          focusedIndex !== undefined ? focusedIndex + 1 : 1,
          0,
          items.length - 1
        );

        if (selectedIndex !== undefined && focusedIndex !== undefined) {
          this.setAriaLiveMessage(
            LEGACY_replaceVariables(legacyTranslations().GRABBED_ITEM_MOVE, [
              actualItems[focusedIndex].text,
              nextSelectedItem + 1,
              items.length
            ])
          );
          this.setItemOrder(defaultReorderHandler(actualItems, focusedIndex, nextSelectedItem));
        }
        this.setFocusedIndex(nextSelectedItem);
        break;
      }
      case 'Escape':
        this.setAriaLiveMessage(
          LEGACY_replaceVariables(legacyTranslations().REORDER_CANCELED, [
            actualItems[focusedIndex].text
          ])
        );

        this.resetState();
        break;
      case 'Enter':
        if (selectedIndex !== undefined && focusedIndex !== undefined) {
          this.setAriaLiveMessage(
            LEGACY_replaceVariables(legacyTranslations().GRABBED_ITEM_DROP, [
              actualItems[focusedIndex].text,
              focusedIndex + 1,
              items.length
            ])
          );

          this.onReorder(selectedIndex, focusedIndex);
        }
        break;
      default:
    }
  };

  handleKeyDownWithRankingOrderAccessibility = (event) => {
    if (RESERVED_KEYS.indexOf(event.key) === -1) {
      return;
    }
    const { focusedIndex, selectedIndex, itemOrder } = this.state;
    const { items } = this.props;
    const actualItems = itemOrder.length ? itemOrder : items;
    event.preventDefault();
    switch (event.key) {
      case ' ':
        /* Space bar will initiate the select operation or drop the item */
        if (selectedIndex !== undefined && focusedIndex !== undefined) {
          this.setAriaLiveMessageWithRankingOrderAccessibility(
            replaceVariables(translations().DND_REORDERING_DROPPED, {
              ITEM: stripHtml(actualItems[focusedIndex].text),
              POSITION: focusedIndex + 1,
              TOTAL: items.length
            })
          );

          this.onReorder(selectedIndex, focusedIndex);
        } else {
          this.setSelectedIndex(focusedIndex);
          this.setAriaLiveMessageWithRankingOrderAccessibility(
            replaceVariables(translations().DND_REORDERING_GRABBED, {
              ITEM: stripHtml(actualItems[focusedIndex].text),
              POSITION: focusedIndex + 1
            })
          );
        }
        break;
      case 'ArrowUp': {
        /* If a selected item exists, decrements position. Always updates focus */
        const prevSelectedItem = clamp(
          focusedIndex !== undefined ? focusedIndex - 1 : 0,
          0,
          items.length - 1
        );
        if (selectedIndex !== undefined && focusedIndex !== undefined) {
          setTimeout(() => {
            this.setAriaLiveMessageWithRankingOrderAccessibility(
              replaceVariables(translations().DND_REORDERING_DRAGGING, {
                ITEM: stripHtml(actualItems[focusedIndex].text),
                POSITION: prevSelectedItem + 1
              })
            );
          }, 0);

          this.setItemOrder(defaultReorderHandler(actualItems, focusedIndex, prevSelectedItem));
        }
        this.setFocusedIndex(prevSelectedItem);
        break;
      }
      case 'ArrowDown': {
        /* If a selected item exists, increments position. Always updates focus */
        const nextSelectedItem = clamp(
          focusedIndex !== undefined ? focusedIndex + 1 : 0,
          0,
          items.length - 1
        );

        if (selectedIndex !== undefined && focusedIndex !== undefined) {
          setTimeout(() => {
            this.setAriaLiveMessageWithRankingOrderAccessibility(
              replaceVariables(translations().DND_REORDERING_DRAGGING, {
                ITEM: stripHtml(actualItems[focusedIndex].text),
                POSITION: nextSelectedItem + 1
              })
            );
          }, 0);
          this.setItemOrder(defaultReorderHandler(actualItems, focusedIndex, nextSelectedItem));
        }

        this.setFocusedIndex(nextSelectedItem);

        break;
      }
      case 'Escape':
        this.setAriaLiveMessageWithRankingOrderAccessibility(
          replaceVariables(translations().DND_REORDERING_CANCELED, {
            ITEM: stripHtml(actualItems[focusedIndex].text)
          })
        );
        setTimeout(() => {
          this.resetState();
          // TODO: This will probably have to be reconsidered when testing with other languages/translations().
        }, 1000);
        break;
      case 'Enter':
        if (selectedIndex !== undefined && focusedIndex !== undefined) {
          this.setAriaLiveMessageWithRankingOrderAccessibility(
            replaceVariables(translations().DND_REORDERING_DROPPED, {
              ITEM: stripHtml(actualItems[focusedIndex].text),
              POSITION: focusedIndex + 1,
              TOTAL: items.length
            })
          );
          this.onReorder(selectedIndex, focusedIndex);
        }
        break;
      default:
    }
  };

  render() {
    const {
      dragStartIndex,
      dropTargetIndex,
      isDraggingDown,
      isDraggingUp,
      itemOrder,
      listRef,
      renderableDropTargetIndex,
      isDragging,
      isValidDropTarget
    } = this.state;

    const { items, children, getPreview, getRef } = this.props;

    const actualItems = itemOrder.length > 0 ? itemOrder : items;

    const placeholderHeight =
      actualItems[dragStartIndex] &&
      getRef(actualItems[dragStartIndex].id).getBoundingClientRect().height;

    const listBoundingReact = listRef ? listRef.getBoundingClientRect() : {};

    const ariaDescribed = !isRankingOrderAccessibilityEnabled()
      ? { 'aria-describedby': 'operation' }
      : {};

    const renderableItems = actualItems.map((item, rowIndex) => {
      const { id, text } = item;

      const showDropPlaceholder =
        isValidDropTarget &&
        dropTargetIndex === rowIndex && // show only for current drop target
        dragStartIndex !== rowIndex && // don't show for dragged item
        dragStartIndex !== renderableDropTargetIndex;

      const showDropPlaceholderBellow = isDraggingDown && showDropPlaceholder;

      const showDropPlaceholderAbove = isDraggingUp && showDropPlaceholder;

      return (
        <ReorderableItem
          key={id + rowIndex}
          rowIndex={rowIndex}
          onDragStart={this.onDragStart}
          onDragTargetUpdate={this.onDragTargetUpdate}
          onReorder={this.onReorder}
          onDrop={this.onDrop}
          ariaDescribedBy="operation"
          selectedIndex={this.state.selectedIndex}
          focusedIndex={this.state.focusedIndex}
          setFocusedIndex={this.setFocusedIndex}
          listRef={listRef}
          actualItems={actualItems}
          text={text}
        >
          {children(
            id,
            isDragging,
            isValidDropTarget,
            placeholderHeight,
            rowIndex,
            showDropPlaceholderAbove,
            showDropPlaceholderBellow,
            text
          )}
        </ReorderableItem>
      );
    });

    return (
      <div>
        {isRankingOrderAccessibilityEnabled() ? (
          <>
            <span
              aria-live="polite"
              role="status"
              aria-atomic="false"
              className={layoutStyles.assistiveText}
              ref={this.liveRegionRef}
            ></span>

            <span id="operation" className={layoutStyles.assistiveText} tabIndex={0}>
              {translations().DND_USAGE_INSTRUCTIONS}
            </span>
          </>
        ) : (
          <>
            <span aria-live="assertive" className={layoutStyles.assistiveText}>
              {this.state.ariaLiveMessage}
            </span>
            <span id="operation" className={layoutStyles.assistiveText} tabIndex={-1}>
              {getTranslation('DRAG_DROP_USAGE')}
            </span>
          </>
        )}

        <ol
          ref={(e) => {
            this.setState({ listRef: e });
          }}
          role="list"
          {...ariaDescribed}
          className={styles.draggable_list}
          onKeyDown={
            isRankingOrderAccessibilityEnabled()
              ? this.handleKeyDownWithRankingOrderAccessibility
              : this.handleKeyDown
          }
          tabIndex={0}
        >
          {renderableItems}
        </ol>

        {this.state.showDragLayer && (
          <CustomDragLayer
            getPreview={getPreview}
            listBoundingReact={listBoundingReact}
            draggedItemHeight={placeholderHeight}
            setIsDragging={this.setIsDragging}
          />
        )}
      </div>
    );
  }
}
